import { COUNTRIES_BY_CODE } from '~/app/shared/util/utilities/countries';
import * as Yup from 'yup';
import { t } from '@lingui/macro';

/**
 * For context, in e-conomic, the phone number is stored in a single field,
 * whereas in payroll, we have two fields: one for the calling code and one for the phone number.
 * Given that the company data in this onboarding step is pre-filled with e-conomic data, we this custom
 * logic to address this difference.
 * It goes as follows:
 *  - Both the calling code and the phone number inputs will always be present (even if they are empty)
 *  - If the phone number has 8 digits, we assume it is a Danish phone number. So the 8 digits go to the
 * number input and we pre-fill the calling code dropdown with the Danish calling code (+45)
 *  - If the phone number already includes a +45, we split that part and fill both inputs accordingly
 *  - If the phone number is anything else, we pre-fill the calling code dropdown with +45 and leave the number input empty
 */
export const treatPhoneNumber = (untreatedPhoneNumber: string) => {
    // Default to Danish calling code
    const defaultCallingCode = COUNTRIES_BY_CODE.DK.callingCode;
    const defaultCallingCodeValue = `${COUNTRIES_BY_CODE.DK.code}#${defaultCallingCode}`;

    // Return default values if phone number is empty
    if (!untreatedPhoneNumber) {
        return { callingCode: defaultCallingCodeValue, number: '' };
    }

    // Clean the phone number by removing spaces and dashes
    const phoneNumber = untreatedPhoneNumber.replace(/[\s-()]/g, '');

    // Check if it starts with Danish calling code
    if (phoneNumber.startsWith(defaultCallingCode)) {
        const phoneNumberWithoutCallingCode = phoneNumber.slice(defaultCallingCode.length);
        return {
            callingCode: defaultCallingCodeValue,
            number: phoneNumberWithoutCallingCode.length === 8 ? phoneNumberWithoutCallingCode : '',
        };
    }

    // Treat as Danish number if it has exactly 8 digits, else, empty number
    return {
        callingCode: defaultCallingCodeValue,
        number: !phoneNumber.includes('+') && phoneNumber.length === 8 ? phoneNumber : '',
    };
};

export const getCompanyInfoStepValidationSchema = () => {
    const requiredFieldMessage = t`Must not be empty`;
    const validEmailMessage = t`Must be a valid email`;

    const requiredStringField = Yup.string().required(requiredFieldMessage);
    const requiredEmailField = requiredStringField.email(validEmailMessage);

    return Yup.object({
        cvrOrSENumber: requiredStringField,
        companyName: requiredStringField,
        emailForInvoicing: requiredEmailField,
        companyCallingCode: requiredStringField,
        companyPhoneNumber: requiredStringField,
        postCode: requiredStringField,
        city: requiredStringField,
        address: requiredStringField,
        contactPersonName: requiredStringField,
        contactPersonCallingCode: requiredStringField,
        contactPersonPhoneNumber: requiredStringField,
        contactPersonEmail: requiredEmailField,
    });
};
